import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, linkedSignal, signal } from '@angular/core';
import { ClarityModule } from '@clr/angular';
import { of } from 'rxjs';
import { DynamicStyleDirective } from 'src/app/directives/dynamic-style.directive';
import { Row, Table } from 'src/app/models/ui/table';
import { CheckInArrayPipe } from 'src/app/pipes/array-methods.pipe';
import { HideIDColumnsPipe } from 'src/app/pipes/hide-idcolumns.pipe';
import { MrTranslatePipe } from 'src/app/pipes/mr-translate.pipe';
import { MrUnitTextPipe } from 'src/app/pipes/mrUnitText.pipe';
import { TablePrettyPrintPipe } from 'src/app/pipes/tablePrettyPrint.pipe';
import { APIService } from 'src/app/services/APIService/api.service';
import { MangelMediaApiFuncService } from 'src/app/services/MediaApiFunc/mangel-media-api-func.service';
import { ImageSlideShowComponent } from '../../_shared/media-slide-show/image-slide-show.component';
import { MediaViewModalComponent } from '../../_shared/mediaviewmodal/mediaviewmodal.component';
import { MangelLogModalComponent } from '../mangel-log-modal/mangel-log-modal.component';
import { IMediaApi } from 'src/app/services/MediaApiFunc/abstract-media-api-func';
import { WartungMediaApiFuncService } from 'src/app/services/MediaApiFunc/wartung-media-api-func.service';
import { rxResource } from '@angular/core/rxjs-interop';
import { FileForPreview } from '../../_shared/preview-thumbnails/preview-thumbnail.type';
import { WartungApiService } from 'src/app/services/APIService/wartung.api.service';
import { ZaehlerApiService } from 'src/app/services/APIService/zaehler.api.service';
import { TransformColNamesPipe } from '../../_overlays/zaehlerverwaltung/transform-col-names.pipe';
import { AusfallColorPipe } from '../../_tabs/zaehlertab/ausfall-color.pipe';
import { OverlayService } from 'src/app/services/Overlay/overlay.service';

interface HistorieSubTree {
  Key: string;
  Value: {
    datum: string;
    id?: number;
  }[];
};

export interface HistorieTree {
  Key: HistorieMode;
  Value: { date: string; id?: number; OTYPID?: number; info?: string }[] | HistorieSubTree[];
};

export interface HistorieNode {
  mode: HistorieMode;
  idOrDate: number | string;
}

type HistorieMode = keyof typeof HistoryIcons;

@Component({
  selector: 'app-historie-tree',
  templateUrl: './historie-tree.component.html',
  styleUrls: ['./historie-tree.component.scss'],
  imports: [
    CommonModule,
    ClarityModule,
    MediaViewModalComponent,
    MangelLogModalComponent,
    ImageSlideShowComponent,
    DynamicStyleDirective,
    MrTranslatePipe,
    TablePrettyPrintPipe,
    MrUnitTextPipe,
    CheckInArrayPipe,
    HideIDColumnsPipe,
    TransformColNamesPipe,
    AusfallColorPipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HistorieTreeComponent {
  private readonly apiService = inject(APIService);
  private readonly wartungAPIService = inject(WartungApiService);
  private readonly zaehlerAPIService = inject(ZaehlerApiService);
  private readonly media_mng = inject(MangelMediaApiFuncService);
  private readonly media_wart = inject(WartungMediaApiFuncService);
  private readonly overlayService = inject(OverlayService);

  public readonly ostammid = input.required<number>();
  protected readonly isLoadingBild = signal(false);
  protected readonly isBildModalOpen = signal(false);

  protected readonly selectedNode = linkedSignal<HistorieNode>(
    () => (this.ostammid(), undefined) /* reset on ostammid change */
  );

  private readonly bilderResource = rxResource({
    params: this.selectedNode,
    stream: () => this.apiService.getBUE_Bilder(this.selectedNode().idOrDate as number),
  });
  protected readonly bilder = linkedSignal(() => this.bilderResource.value() ?? Array(8));


  protected readonly tol = this.zaehlerAPIService.getToleranzenBezeichnungen();

  private readonly historieResource = rxResource<HistorieTree[], number>({
    params: this.ostammid,
    stream: ({ params: ostammid }) => this.apiService.getAnlagenHistorie(ostammid),
  });
  protected readonly historieTree = this.historieResource.value.asReadonly();
  protected readonly historieLoading = this.historieResource.isLoading;

  private readonly tableResource = rxResource<Table, HistorieNode>({
    params: this.selectedNode,
    stream: ({ params }) => this.apiService.getHistoryItem(this.ostammid(), params),
  });
  protected readonly nodeTable = this.tableResource.value.asReadonly();
  protected readonly tableLoading = this.tableResource.isLoading;

  private readonly filesResource = rxResource<FileForPreview[], Table>({
    params: this.nodeTable,
    stream: ({ params: table }) => {
      const { mode, idOrDate } = this.selectedNode();
      switch (mode) {
        case 'inspektionen': {
          const pruefbild = table?.rows?.[0]?.pruefbild as string;
          return pruefbild ? this.apiService.getPassBilder(pruefbild) : of(null);
        }
        case 'wartungen': {
          const wID = +idOrDate;
          return this.wartungAPIService.getWartungMediaByID(wID);
        }
        default:
          return of(null);
      }
    },
  });
  protected readonly files = this.filesResource.value.asReadonly();

  protected readonly selectedRow = signal<Row | null>(null);

  protected readonly mediaViewInfo = computed<IMediaApi>(() => {
    const row = this.selectedRow();
    if (!row) return null;
    switch (this.selectedNode().mode) {
      case 'stoerungen':
      case 'instandsetzungen':
      case 'sonderinspektion':
      case 'inspektionen': {
        return {
          sources: this.media_mng.parseMediaPath(row.bilder as string, row.rueckbilder as string),
          getApiFunctionMedia: this.media_mng.getApiFunctionMedia,
          getApiFunctionMediaBig: this.media_mng.getApiFunctionMediaBig,
        };
      }
      case 'wartungen': {
        return {
          sources: this.media_wart.parseMediaPath(row.RevBild as string),
          getApiFunctionMedia: this.media_wart.getApiFunctionMedia,
          getApiFunctionMediaBig: this.media_wart.getApiFunctionMediaBig,
        };
      }
    }
  });

  protected getMessungsUebersicht(value) {
    this.overlayService.setOverlay({
      overlay: 'openMessungsuebersicht',
      id: this.ostammid(),
      pruefid: value.id,
      bild: value.Bild,
      LPDatum: value.date,
      zIndex: 8,
    });
  }

  protected mangelLog = signal<number>(null);

  protected readonly icons = HistoryIcons;
}

enum HistoryIcons {
  inspektionen = 'search',
  begehung = 'clock',
  sonderinspektion = 'warning-standard',
  herstellerdaten = 'home',
  messungen = 'ruler-pencil',
  sperrungen = 'times',
  stoerungen = 'exclamation-triangle',
  langsamfahrstellen = 'lastelle',
  instandsetzungen = 'wrench',
  wartungen = 'wartung',
  bahnuebergang = 'bahnuebergang',
  archiv = 'library',
}
